<script setup lang="ts">
  import { computed } from "vue";
  import { Text, Button, IconListCompleted } from "@ui-kit";
  import { type OrderType } from "@src/api";
  import { useWidgetsStore } from "@src/store";
  import { useOrderCreate } from "@src/hooks";

  const emit = defineEmits<{ (e: "close"): void }>();

  const order_create = useOrderCreate();
  const widgets_store = useWidgetsStore();

  type SuccessViewConfig = {
    id: string;
    title: string;
    subtitle: string;
    show_order_number: boolean;
  };

  const success_view_config_by_type: Record<Exclude<OrderType, "pickup.device">, SuccessViewConfig> = {
    "pickup": {
      id: "pickup",
      title: "Запрос создан",
      subtitle: "Сообщите клиенту номер заказа, его будет проще найти при необходимости",
      show_order_number: true,
    },
    "online": {
      id: "online",
      title: "Запрос создан",
      subtitle: "Сообщите клиенту номер заказа, его будет проще найти при необходимости",
      show_order_number: true,
    },
    "pickup.commerce": {
      id: "pickup.commerce",
      title: "Коммерческий запрос создан",
      subtitle: "После сборки его отнесут в соответствующую зону",
      show_order_number: false,
    },
  };

  const success_view_config = computed(() => success_view_config_by_type[order_create.value.type as OrderType]);

  const handleClickToOrders = () => {
    emit("close");
    widgets_store.open("orders");
  };

  const numbers = computed(() => order_create.value.order_number.split("-")[1].split(""));
</script>

<template>
  <div class="order-create-success" data-testid="order-create-success">
    <div class="content">
      <IconListCompleted />
      <div class="description">
        <Text font="heading-H4" :data-testid="`order-create-success-title-${success_view_config.id}`">
          {{ $t(success_view_config.title) }}
        </Text>
        <Text color="secondary" :data-testid="`order-create-success-subtitle-${success_view_config.id}`">
          {{ $t(success_view_config.subtitle) }}
        </Text>
      </div>
      <div
        v-if="success_view_config.show_order_number && numbers.length > 0"
        class="order-number"
        data-testid="order-create-success-order-number"
      >
        <div
          v-for="(number, index) in numbers"
          :key="index"
          class="order-number-item"
          :data-testid="`order-create-success-order-number-item`"
        >
          <Text font="heading-H3">{{ number }}</Text>
        </div>
      </div>
    </div>
    <Button variant="secondary" @click="emit('close')" data-testid="order-create-success-to-main-btn">
      {{ $t("На главную") }}
    </Button>
    <Button @click="handleClickToOrders" data-testid="order-create-success-to-tasks-btn">{{ $t("В запросы") }}</Button>
  </div>
</template>

<style scoped>
  .order-create-success {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 8px;
    padding: 16px;

    .content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      flex: 1;
    }

    .description {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 8px;
      margin-top: 24px;
    }

    .order-number {
      display: flex;
      gap: 8px;
      margin-top: 16px;
    }

    .order-number-item {
      border-radius: 16px;
      height: 86px;
      width: 62px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: var(--sf-base-background-primary);
    }

    & :deep(.button) {
      width: 100%;
    }
  }
</style>
